/**
 * Async iterator helper for endpoints that paginate via skip/limit. Lets
 * module code expose a `stream(params)` method without re-implementing the
 * loop each time.
 *
 * Usage from a module:
 *
 *   async *stream(params: FreeProxyListParams) {
 *     yield* paginate({
 *       fetchPage: (skip) => this.fetchPage({ ...params, skip }),
 *       extractItems: (page) => page.proxies,
 *       hasMore: (page) => page.nextpage,
 *       pageSize: 2000,
 *     });
 *   }
 */
export interface PaginateOptions<Page, Item> {
  fetchPage: (skip: number) => Promise<Page>;
  extractItems: (page: Page) => Item[];
  hasMore: (page: Page) => boolean;
  pageSize: number;
  /** Safety stop in case the API misreports `hasMore` forever. */
  maxPages?: number;
}

export async function* paginate<Page, Item>(
  opts: PaginateOptions<Page, Item>,
): AsyncGenerator<Item, void, void> {
  const maxPages = opts.maxPages ?? 1000;
  let skip = 0;
  for (let i = 0; i < maxPages; i++) {
    const page = await opts.fetchPage(skip);
    const items = opts.extractItems(page);
    for (const item of items) yield item;
    if (!opts.hasMore(page) || items.length === 0) return;
    skip += opts.pageSize;
  }
}
